import { FaCircle } from "react-icons/fa";
import { Loader } from "../../../../../components";
import styles from "./tabs.module.scss";
import { Button, DatePicker, Input, message, Modal, Tooltip } from "antd";
import { useState } from "react";
import dayjs from "dayjs";
import {
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import {
  getColorFromRecommendation,
  getColorFromApplicantRecommendation,
  parseLocalDateComponents,
} from "../../easyapp.utils";
import { asyncUpdateAppData } from "../../../../../store/easyApp/easyapp-async";
import { useAppDispatch } from "../../../../../store/hooks";

const GroupTab = ({
  applicants,
  applicantIdv,
  applicantPayrollIncome,
  applicantBankIncome,
  loading,
  screening,
  screeningResults,
}: any) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useAppDispatch();

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const InfoSection = ({
    title,
    description,
    children,
    action,
  }: {
    title: string;
    description: string;
    children: React.ReactNode;
    action?: React.ReactNode;
  }) => (
    <div className={styles.application}>
      <div className={styles.rightsection}>
        <p className={styles.name}>{title}</p>
        <p className={styles.detail2}>{description}</p>
        {action && <div style={{ marginTop: "12px" }}>{action}</div>}
      </div>
      <div className={styles.leftsection}>{children}</div>
    </div>
  );

  const InfoItemDisplay = ({
    label,
    value,
    format,
    subValue,
    onSave,
  }: InfoItem & { onSave?: (val: string) => void }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedValue, setEditedValue] = useState(value?.toString() || "");
    const [isUpdating, setIsUpdating] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const renderDisplayValue = () => {
      if (format === "date" && value) {
        // Parse date string using local timezone to prevent day shifts
        const components = parseLocalDateComponents(value as string);
        if (!components) return "N/A";

        const { year, month: monthNum, day } = components;
        // Create a date object just to get the month name
        const tempDate = new Date(year, monthNum - 1, day);
        const month = tempDate.toLocaleString("en-US", { month: "long" });
        const suffix = (d: number) =>
          d > 3 && d < 21 ? "th" : ["st", "nd", "rd"][(d % 10) - 1] || "th";
        return `${month} ${day}${suffix(day)}, ${year}`;
      }

      if (format === "currency" && typeof value === "number") {
        return `$${value.toFixed(2)}`;
      }

      if (format === "annualCurrency") {
        return `$${value}/yr`;
      }

      if (format === "boolean") return value ? "Yes" : "No";

      if (format === "title-case" && typeof value === "string") {
        return value
          .split("-")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
      }

      return value?.toString() || "N/A";
    };

    const renderEditField = () => {
      if (format === "date") {
        return (
          <DatePicker
            value={dayjs(editedValue)}
            onChange={(date) => setEditedValue(date?.toISOString() || "")}
          />
        );
      }

      return (
        <Input
          value={editedValue}
          onChange={(e) => setEditedValue(e.target.value)}
        />
      );
    };

    const handleSave = async () => {
      setIsUpdating(true);
      try {
        await onSave?.(editedValue);
        setIsSuccess(true);
        message.success("Info Updated Successfully")
        setTimeout(() => {
          setIsEditing(false);
          setIsSuccess(false);
        }, 1000);
      } catch (error) {
        message.error("Failed to Update");
      } finally {
        setIsUpdating(false);
      }
    };

    return (
      <div className={styles.wrapper}>
        <p className={styles.detail}>{label}</p>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isEditing ? (
            <>
              {renderEditField()}
              <Button
                size="small"
                style={{ width: "50px" }}
                icon={
                  isSuccess ? (
                    <CheckCircleFilled style={{ color: "#52c41a" }} />
                  ) : (
                    <CheckOutlined />
                  )
                }
                onClick={handleSave}
                loading={isUpdating}
                disabled={isSuccess}
              />
              <Button
                size="small"
                style={{ width: "50px" }}
                icon={<CloseOutlined />}
                onClick={() => {
                  setIsEditing(false);
                  setEditedValue(value?.toString() || "");
                }}
              />
            </>
          ) : (
            <>
              <div className={styles.editableField}>
                <p style={{ margin: 0 }}>{renderDisplayValue()}</p>
                {onSave && (
                  <Tooltip title="Edit">
                    <EditOutlined
                      className={styles.editIcon}
                      onClick={() => setIsEditing(true)}
                    />
                  </Tooltip>
                )}
              </div>
            </>
          )}
        </div>
        {subValue && (
          <p className={styles.subDetail}>{subValue?.toString() || "N/A"}</p>
        )}
      </div>
    );
  };

  const RecommendationDisplay = ({
    label,
    recommendation,
    message,
    tooltip,
  }: {
    label: any;
    recommendation?: any;
    message: string;
    tooltip?: React.ReactNode;
  }) => (
    <div style={{ height: "80px" }}>
      {tooltip ? (
        <Tooltip title={tooltip}>
          <p className={styles.detail} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            {label}
          </p>
        </Tooltip>
      ) : (
        <p className={styles.detail}>{label}</p>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <FaCircle
          style={{
            color: recommendation
              ? getColorFromRecommendation(recommendation)
              : getColorFromApplicantRecommendation(message),
          }}
        />
        <p>{message || "Pending"}</p>
      </div>
    </div>
  );

  if (loading) {
    return <Loader />;
  }

  if (!applicants) {
    return <p>Applicant data not available</p>;
  }

  const updateField = async (field: string, value: string) => {
    try {
      await dispatch(
        asyncUpdateAppData({
          fieldName: field,
          value,
          applicationId: applicants.application_id,
        })
      ).unwrap();
    } catch (error) {}
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <InfoSection
          title="Apartment Information"
          description="Basic Information about the applicant, including contact information."
        >
          <div className={styles.appdetails}>
            <div>
              <InfoItemDisplay
                label="First Name"
                value={applicants.first_name || "N/A"}
                onSave={(val) => updateField("first_name", val)}
              />
              <InfoItemDisplay
                label="Phone"
                value={applicants.phone_number || "N/A"}
                onSave={(val) => updateField("phone_number", val)}
              />
              <InfoItemDisplay
                label="Social Security Number"
                value={applicants.social_security_number || "N/A"}
                onSave={(val) => updateField("social_security_number", val)}
              />
            </div>
            <div>
              <InfoItemDisplay
                label="Last Name"
                value={applicants.last_name || "N/A"}
                onSave={(val) => updateField("last_name", val)}
              />
              <InfoItemDisplay
                label="Email"
                value={applicants.email || "N/A"}
                onSave={(val) => updateField("email", val)}
              />
              <InfoItemDisplay
                label="Employment status"
                value={applicants.employment_status || "N/A"}
                onSave={(val) => updateField("employment_status", val)}
              />
            </div>
            <div>
              <InfoItemDisplay
                label="Birthday"
                value={applicants.dob}
                format="date"
                onSave={(val) => updateField("dob", val)}
              />
              <InfoItemDisplay
                label="Other names (aliases)"
                value={applicants.other_names || "None"}
                onSave={(val) => updateField("other_names", val)}
              />
              <InfoItemDisplay
                label="Annual Income"
                value={applicants.employment_income || 0}
                format="annualCurrency"
                onSave={(val) => updateField("employment_income", val)}
              />
            </div>
          </div>
        </InfoSection>

        <InfoSection
          title="Children"
          description="List names of any children living in the property with this applicant."
        >
          {(() => {
            // Support both 'children' and 'minors' field names
            const childrenData = applicants.children || applicants.minors || [];
            
            // Normalize children data to always be array of objects
            const normalizedChildren = Array.isArray(childrenData)
              ? childrenData.map((child: any) => {
                  // If child is a string (e.g., "Emily Doe"), parse it
                  if (typeof child === "string") {
                    const nameParts = child.trim().split(" ");
                    return {
                      first_name: nameParts[0] || "",
                      last_name: nameParts.slice(1).join(" ") || "",
                      dob: null,
                    };
                  }
                  // If child is already an object, return it as-is
                  return child;
                })
              : [];

            if (normalizedChildren.length > 0) {
              return normalizedChildren.map((child: any, index: number) => (
                <div key={index}>
                  <p className={styles.index}>Child #{index + 1}</p>
                  <div className={styles.appdetails}>
                    <div>
                      <InfoItemDisplay
                        label="First Name"
                        value={child?.first_name || child?.firstName || "N/A"}
                      />
                    </div>
                    <div>
                      <InfoItemDisplay
                        label="Last Name"
                        value={child?.last_name || child?.lastName || "N/A"}
                      />
                    </div>
                    <div>
                      <InfoItemDisplay
                        label="Birthday"
                        value={child?.dob || child?.dateOfBirth}
                        format="date"
                        subValue={
                          child?.dob || child?.dateOfBirth
                            ? `${
                                new Date().getFullYear() -
                                new Date(child.dob || child.dateOfBirth).getFullYear()
                              } years old`
                            : "N/A"
                        }
                      />
                    </div>
                  </div>
                </div>
              ));
            }

            return (
              <div className={styles.appdetails}>
                <div></div>
                <div>
                  <p style={{ fontSize: "16px" }}>No Children</p>
                </div>
              </div>
            );
          })()}
        </InfoSection>

        <InfoSection
          title="Current Residence"
          description="The address, rent, and contact information for their previous landlord."
        >
          <div className={styles.appdetails}>
            <div>
              <InfoItemDisplay
                label="Current address"
                value={
                  [
                    applicants.current_residence_address,
                    applicants.current_residence_city,
                    applicants.current_residence_state,
                    applicants.current_residence_zip,
                  ]
                    .filter(Boolean)
                    .join(", ") || "N/A"
                }
              />
              <InfoItemDisplay
                label="Reason for leaving"
                value={applicants.current_residence_reason_for_leaving || "N/A"}
                format="title-case"
              />
            </div>
            <div>
              <InfoItemDisplay
                label="Monthly Rent"
                value={`$${applicants.current_residence_rent || 0}/mo`}
              />
              <InfoItemDisplay
                label="Landlord name"
                value={applicants.current_residence_landlord_name || "N/A"}
              />
            </div>
            <div>
              <InfoItemDisplay
                label="Started living there"
                value={applicants.current_residence_start_date}
                format="date"
              />
              <InfoItemDisplay
                label="Landlord phone number"
                value={applicants.current_residence_landlord_phone || "N/A"}
              />
            </div>
          </div>
        </InfoSection>

        <InfoSection
          title="Emergency Contact #1"
          description="The first person they listed as an emergency contact."
        >
          <div className={styles.appdetails}>
            <div>
              <InfoItemDisplay
                label="Contact name"
                value={applicants.emergency_contact_name || "N/A"}
              />
            </div>
            <div>
              <InfoItemDisplay
                label="Contact phone"
                value={applicants.emergency_contact_phone || "N/A"}
              />
            </div>
            <div>
              <InfoItemDisplay
                label="Contact relationship"
                value={applicants.emergency_contact_relationship || "N/A"}
              />
            </div>
          </div>
        </InfoSection>

        <InfoSection
          title="Emergency Contact #2"
          description="The second person they listed as an emergency contact."
        >
          <div className={styles.appdetails}>
            <div>
              <InfoItemDisplay
                label="Contact name"
                value={applicants.emergency_contact2_name || "N/A"}
              />
            </div>
            <div>
              <InfoItemDisplay
                label="Contact phone"
                value={applicants.emergency_contact2_phone || "N/A"}
              />
            </div>
            <div>
              <InfoItemDisplay
                label="Contact relationship"
                value={applicants.emergency_contact2_relationship || "N/A"}
              />
            </div>
          </div>
        </InfoSection>

        <InfoSection
          title="Miscellaneous"
          description="Other information that doesn't fit into one of the above categories."
        >
          <div className={styles.appdetails}>
            <div>
              <InfoItemDisplay
                label="Smoker"
                value={applicants.misc_smoker ? "Smoker" : "Non-Smoker"}
              />
              <InfoItemDisplay
                label="Criminal Record"
                value={
                  applicants.misc_crimes ? applicants.misc_crimes_details : "No"
                }
              />
            </div>
            <div>
              <InfoItemDisplay
                label="Marital status"
                value={applicants.misc_marital_status || "N/A"}
              />
              <InfoItemDisplay
                label="Filed for bankruptcy"
                value={
                  applicants.misc_bankruptcy
                    ? applicants.misc_bankruptcy_details
                    : "No"
                }
              />
            </div>
            <div>
              <InfoItemDisplay
                label="Facebook profile"
                value={
                  applicants.misc_has_facebook_profile
                    ? applicants.misc_facebook_profile
                    : "N/A"
                }
              />
              <InfoItemDisplay
                label="US citizens?"
                value={applicants.misc_legal_residents ? "Yes" : "No"}
              />
            </div>
          </div>
        </InfoSection>

        <InfoSection
          title="Vehicles"
          description="List of vehicles owned by the applicant."
        >
          {Array.isArray(applicants.vehicles) &&
          applicants.vehicles.length > 0 ? (
            applicants.vehicles.map((vehicle: any, index: number) => (
              <div key={index}>
                <p className={styles.index}>Vehicle #{index + 1}</p>
                <div className={styles.appdetails}>
                  <div>
                    <InfoItemDisplay
                      label="Make"
                      value={vehicle?.make || "N/A"}
                    />
                    <InfoItemDisplay
                      label="Color"
                      value={vehicle?.color || "N/A"}
                    />
                  </div>
                  <div>
                    <InfoItemDisplay
                      label="Model"
                      value={vehicle?.model || "N/A"}
                    />
                    <InfoItemDisplay
                      label="License plate number"
                      value={vehicle?.license_plate || "N/A"}
                    />
                  </div>
                  <div>
                    <InfoItemDisplay
                      label="Year"
                      value={vehicle?.year || "N/A"}
                    />
                    <InfoItemDisplay
                      label="Insurance Company"
                      value={vehicle?.insurance_company || "N/A"}
                    />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.appdetails}>
              <div></div>
              <div>
                <p style={{ fontSize: "16px" }}>No Vehicles</p>
              </div>
            </div>
          )}
        </InfoSection>

        <InfoSection
          title="Pets"
          description="List of pets owned by the applicant."
        >
          {Array.isArray(applicants.pets) && applicants.pets.length > 0 ? (
            applicants.pets.map((pet: any, index: number) => (
              <div key={index}>
                <p className={styles.index}>Pet #{index + 1}</p>
                <div className={styles.appdetails}>
                  <div>
                    <InfoItemDisplay
                      label="Pet Name"
                      value={pet?.name || "N/A"}
                    />
                    <InfoItemDisplay label="Age" value={pet?.age || "N/A"} />
                    <InfoItemDisplay label="Sex" value={pet?.sex || "N/A"} />
                  </div>
                  <div>
                    <InfoItemDisplay
                      label="What kind of animal?"
                      value={pet?.type || "N/A"}
                    />
                    <InfoItemDisplay
                      label="Weight"
                      value={pet?.weight || "N/A"}
                    />
                    <InfoItemDisplay
                      label="Neutered?"
                      value={pet?.neutered ? "Yes" : "No"}
                    />
                  </div>
                  <div>
                    <InfoItemDisplay
                      label="Breed"
                      value={pet?.breed || "N/A"}
                    />
                    <InfoItemDisplay
                      label="Color"
                      value={pet?.color || "N/A"}
                    />
                    <InfoItemDisplay
                      label="Declawed?"
                      value={pet?.declawed ? "Yes" : "No"}
                    />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.appdetails}>
              <div></div>
              <div>
                <p style={{ fontSize: "16px" }}>No Pets</p>
              </div>
            </div>
          )}
        </InfoSection>

        <InfoSection
          title="Plaid Verification"
          description="Information produced from the Plaid scan done on each applicant."
          action={
            applicantIdv?.id ? (
              <Button
                type="link"
                href={`https://dashboard.plaid.com/identity_verification/sessions/${applicantIdv.id}?status=${applicantIdv.status || ''}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ padding: 0 }}
              >
                View IDV in Plaid Dashboard →
              </Button>
            ) : null
          }
        >
          {applicantPayrollIncome || applicantIdv || applicantBankIncome ? (
            <>
              {applicantPayrollIncome?.jobs.length > 0 && (
                <div>
                  <InfoItemDisplay
                    label="Total income from Plaid"
                    value={`$${applicantPayrollIncome?.ytdIncome ?? 0}/yr`}
                  />
                </div>
              )}
              {applicantPayrollIncome?.jobs?.length > 0 &&
                applicantPayrollIncome.jobs.map((job: any, index: number) => (
                  <div key={index}>
                    <p className={styles.index}>Job #{index + 1}</p>
                    <div className={styles.appdetails}>
                      <div>
                        <InfoItemDisplay
                          label="Employer name"
                          value={job.employerName ? job.employerName : "N/A"}
                        />
                        <InfoItemDisplay
                          label="Hourly/salary rate"
                          value={job.payRate ? job.payRate : "N/A"}
                        />
                      </div>
                      <div>
                        <InfoItemDisplay
                          label="Employer address"
                          value={
                            job.employerAddress ? job.employerAddress : "N/A"
                          }
                        />
                        <InfoItemDisplay
                          label="Recent pay periods"
                          value={
                            job.payPeriod
                              ? `${job.payPeriod} $${job.netPay} net | $${job.grossEarnings} gross`
                              : "N/A"
                          }
                        />
                      </div>
                      <div>
                        <InfoItemDisplay
                          label="Pay Frequency"
                          value={job.payFrequency ? job.payFrequency : "N/A"}
                        />
                        <InfoItemDisplay
                          label="Year-to-date totals"
                          value={job.ytdNet ? job.ytdNet : "N/A"}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              {applicantBankIncome && (
                <>
                  <div className={styles.appdetails}>
                    <div>
                      <InfoItemDisplay
                        label="Monthly Income"
                        value={`$${
                          applicantBankIncome?.included_income || 0
                        }/mo`}
                      />
                    </div>
                    <div>
                      <InfoItemDisplay
                        label="Excluded Income"
                        value={`$${
                          applicantBankIncome?.excluded_income || 0
                        }/mo`}
                      />
                    </div>
                  </div>
                  {applicantBankIncome.included_sources?.length > 0 && (
                    <>
                      {applicantBankIncome.included_sources.map(
                        (source: any, index: number) => (
                          <div key={index}>
                            <p className={styles.index}>
                              Included Source #{index + 1}
                            </p>
                            <div className={styles.appdetails}>
                              <div>
                                <InfoItemDisplay
                                  label="Category"
                                  value={source.category || "N/A"}
                                />
                              </div>
                              <div>
                                <InfoItemDisplay
                                  label="Description"
                                  value={source.description || "N/A"}
                                />
                              </div>
                              <div>
                                <InfoItemDisplay
                                  label="Monthly Amount"
                                  value={source.monthly_amount || "N/A"}
                                />
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </>
                  )}
                  {applicantBankIncome.excluded_sources?.length > 0 && (
                    <>
                      {applicantBankIncome.excluded_sources.map(
                        (source: any, index: number) => (
                          <div key={index}>
                            <p className={styles.index}>
                              Excluded Source #{index + 1}
                            </p>
                            <div className={styles.appdetails}>
                              <div>
                                <InfoItemDisplay
                                  label="Category"
                                  value={source.category || "N/A"}
                                />
                                <InfoItemDisplay
                                  label="Reason for Exclusion"
                                  value={source.reason?.toUpperCase() || "N/A"}
                                />
                              </div>
                              <div>
                                <InfoItemDisplay
                                  label="Description"
                                  value={source.description || "N/A"}
                                />
                              </div>
                              <div>
                                <InfoItemDisplay
                                  label="Monthly Amount"
                                  value={`$${source.monthly_amount || 0}/mo`}
                                />
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </>
                  )}
                </>
              )}
              {applicantIdv ? (
                <>
                  {/* Overall IDV Status */}
                  <p className={styles.index}>Overall Verification Status</p>
                  <div className={styles.appdetails}>
                    <RecommendationDisplay
                      label="Identity Verification Result"
                      recommendation={applicantIdv?.status === "success" ? 1 : 7}
                      message={
                        applicantIdv?.status === "success"
                          ? "Passed"
                          : applicantIdv?.status === "failed"
                          ? "Failed"
                          : applicantIdv?.status || "Pending"
                      }
                    />
                    {applicantIdv?.documentary_verification && (
                      <RecommendationDisplay
                        label="Document Verification"
                        recommendation={
                          applicantIdv?.documentary_verification?.status === "success"
                            ? 1
                            : 7
                        }
                        message={
                          applicantIdv?.documentary_verification?.status === "success"
                            ? "Valid"
                            : applicantIdv?.documentary_verification?.status || "Invalid"
                        }
                      />
                    )}
                    {applicantIdv?.risk_check && (
                      <RecommendationDisplay
                        label="Risk Check"
                        recommendation={
                          applicantIdv?.risk_check?.status === "success" ? 1 : 7
                        }
                        message={
                          applicantIdv?.risk_check?.status === "success"
                            ? "Passed"
                            : applicantIdv?.risk_check?.status || "Failed"
                        }
                      />
                    )}
                  </div>

                  {/* KYC Check Details */}
                  {applicantIdv?.kyc_check && (
                    <>
                      <p className={styles.index}>ID Check</p>
                      <div className={styles.appdetails}>
                        <RecommendationDisplay
                          label="ID authenticity check"
                          recommendation={
                            applicantIdv?.kyc_check?.id_number?.summary === "match"
                              ? 1
                              : 7
                          }
                          message={
                            applicantIdv?.kyc_check?.id_number?.summary === "match"
                              ? "Valid"
                              : "Invalid"
                          }
                        />
                        <RecommendationDisplay
                          label="Name check"
                          recommendation={
                            applicantIdv?.kyc_check?.name?.summary === "match"
                              ? 1
                              : 7
                          }
                          message={
                            applicantIdv?.kyc_check?.name?.summary === "match"
                              ? "Valid"
                              : "Invalid"
                          }
                        />
                        <RecommendationDisplay
                          label="DOB check"
                          recommendation={
                            applicantIdv?.kyc_check?.date_of_birth?.summary ===
                            "match"
                              ? 1
                              : 7
                          }
                          message={
                            applicantIdv?.kyc_check?.date_of_birth?.summary ===
                            "match"
                              ? "Valid"
                              : "Invalid"
                          }
                        />
                        <RecommendationDisplay
                          label="Address check"
                          recommendation={
                            applicantIdv?.kyc_check?.address?.summary === "match"
                              ? 1
                              : 7
                          }
                          message={
                            applicantIdv?.kyc_check?.address?.summary === "match"
                              ? "Valid"
                              : "Invalid"
                          }
                        />
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className={styles.appdetails}>
                  <div></div>
                  <div>
                    <p style={{ fontSize: "16px" }}>Id Verification Pending</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className={styles.appdetails}>
              <div></div>
              <div>
                <p style={{ fontSize: "16px" }}>Verification Pending</p>
              </div>
            </div>
          )}
        </InfoSection>

        {screening &&
        screeningResults &&
        Object.keys(screening).length > 0 &&
        Object.keys(screeningResults).length > 0 ? (
          <InfoSection
            title="Transunion"
            description="Information produced from the Transunion scan done on each applicant."
          >
            <div className={styles.appdetails}>
              <RecommendationDisplay
                label="Transunion Recommendation"
                recommendation={screeningResults?.ScoreResult?.Recommendation}
                message={screeningResults?.ScoreResult?.RecommendationMessage}
                tooltip={
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      Visit TransUnion TrueVision to view detailed resident screening reports.
                    </div>
                    {screeningResults?.ApplicationNumber && (
                      <a 
                        href={`https://residentscreening.transunion.com/Result/${screeningResults.ApplicationNumber}?redirect=true`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1890ff', fontWeight: 500 }}
                      >
                        View in TrueVision Dashboard →
                      </a>
                    )}
                  </div>
                }
              />
              <RecommendationDisplay
                label="Criminal Recommendation"
                message={screening?.CriminalRecommendation}
              />
              <RecommendationDisplay
                label="Eviction Recommendation"
                message={screening?.EvictionRecommendation}
              />
              <RecommendationDisplay
                label="Application Recommendation"
                recommendation={
                  screeningResults?.ScoreResult?.ApplicationRecommendation
                }
                message={
                  screeningResults?.ScoreResult
                    ?.ApplicationRecommendationMessage
                }
              />
              <InfoItemDisplay
                label="Application Score"
                value={screening?.ScoreResult?.ApplicantScore || "N/A"}
              />
              <InfoItemDisplay
                label="Adverse Factor"
                value={screeningResults?.ScoreResult?.AdverseFactors || "N/A"}
              />
              <InfoItemDisplay
                label="Caution Note"
                value={screening?.ScoreResult?.CautionNotes?.[0] || "N/A"}
              />
              <InfoItemDisplay
                label="Policy Message"
                value={screeningResults?.ScoreResult?.PolicyMessage || "N/A"}
              />
              <InfoItemDisplay
                label="Status Message"
                value={screening?.ScoreResult?.StatusMessage || "N/A"}
              />
              <div className={styles.wrapper}>
                <p className={styles.detail} style={{ marginBottom: 6 }}>Background Report</p>
                <Button type="primary" onClick={showModal}>
                  View Background Report
                </Button>
                <Modal
                  title="Background Report"
                  open={isModalOpen}
                  onOk={handleOk}
                  onCancel={handleCancel}
                  width={900}
                  footer={[
                    <Button key="close" onClick={handleCancel}>
                      Close
                    </Button>,
                  ]}
                >
                  {screeningResults?.BackgroundReport ? (
                    <iframe
                      srcDoc={screeningResults.BackgroundReport}
                      style={{
                        width: "100%",
                        minHeight: "600px",
                        border: "none",
                      }}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
                    />
                  ) : (
                    <p>No background report available</p>
                  )}
                </Modal>
              </div>
            </div>
          </InfoSection>
        ) : null}
      </div>
    </div>
  );
};

export default GroupTab;
